import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";

/**
 * RelatedProducts — "You may also like" strip for PDP.
 * Shows other IX tiers (excluding current), max 4 cards.
 */
export default function RelatedProducts({ currentSlug }) {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    }, []);

    const CORE_IX_ORDER = [
        "signia-styletto-1ix",
        "kit-styletto-1ix",
        "kit-styletto-2ix",
        "kit-styletto-3ix",
        "kit-styletto-5ix",
        "kit-styletto-7ix",
    ];
    const bySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
    const suggestions = CORE_IX_ORDER
        .map((s) => bySlug[s])
        .filter((p) => p && p.slug !== currentSlug)
        .slice(0, 4);

    if (suggestions.length === 0) return null;

    return (
        <section className="py-14 border-t border-[#E5E7EB]" data-testid="related-products-section">
            <div className="container-x">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <p className="eyebrow">You may also like</p>
                        <h2 className="font-serif text-[32px] md:text-[40px] leading-[1.05] mt-2">
                            Other Styletto IX <span className="italic text-[#003E4D]">tiers to consider</span>
                        </h2>
                    </div>
                    <Link to="/compare" className="btn-secondary self-start" data-testid="related-full-compare">
                        Compare all <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {suggestions.map((p) => (
                        <Link
                            key={p.slug}
                            to={`/products/${p.slug}`}
                            className="group block bg-white rounded-2xl border border-black/5 overflow-hidden hover:border-[#003E4D]/40 hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.2)] transition"
                            data-testid={`related-card-${p.slug}`}
                        >
                            <div className="aspect-square bg-[#f3f4f6] overflow-hidden">
                                <img
                                    src={p.image}
                                    alt={p.name}
                                    className="w-full h-full object-contain p-4 group-hover:scale-[1.05] transition-transform duration-500"
                                    loading="lazy"
                                />
                            </div>
                            <div className="p-4">
                                <p className="eyebrow text-[10px]">{p.technology_level}</p>
                                <h3 className="font-serif text-[17px] mt-1.5 leading-tight">{p.short_name || p.name}</h3>
                                <div className="mt-3 flex items-baseline justify-between">
                                    <span className="text-[15px] font-semibold text-[#003E4D]">{formatINR(p.price)}</span>
                                    <span className="text-[11.5px] text-[#4a5568] group-hover:text-[#003E4D]">View →</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
